import { Component } from 'react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { inject, observer } from 'mobx-react';

//components
import withLocalization from '~/hoc/withLocalization';
import applicationRouter from '~/hoc/applicationRouter';
import GenericList from '~/components/GenericList/GenericList';
import GenericFilter from '~/components/GenericList/GenericFilter.js';
import MemberInfoCard from '~/components/Card/MemberInfoCard.jsx';
import AddPaidOvertimeModal from './AddPaidOvertimeModal';
import ViewOvertimeTransactionModal from './ViewOvertimeTransactionModal';
import Button from '../../../components/CustomButton/CustomButton.jsx';

//elements
import LoadingSpinner from '../../../elements/LoadingSpinner.js';
import StatPill from '../../../elements/StatPill.js';
import StatSliderPill from '../../../elements/StatSliderPill.js';
import StatusBadge from '../../../elements/StatusBadge.js';
import Select from '~/elements/Select';

//utils
import { shortDateFormat, dateToTimeFormat, totalTimeFormat } from '~/utils/dateFormat';
import { generateYearOptions } from '~/utils/timeCalc';
import {
    overtime_transaction_types,
    overtime_transaction_type_map,
    overtime_transaction_type_color_map,
    overtime_transaction_overtime_types,
    overtime_transaction_overtime_type_map,
    overtime_transaction_overtime_type_color_map,
    overtime_transaction_status_map,
    overtime_transaction_status_color_map,
    user_type_map
} from '../../../utils/normalStatuses.js';

class OvertimeBankList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showAddModal: false,
            viewTransactionId: props.transactionId || null,
            viewTransactionGroup: null,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.transactionId && this.props.transactionId !== prevProps.transactionId) {
            this.setState({ viewTransactionId: this.props.transactionId, viewTransactionGroup: null });
        }
    }

    handleViewRow = (row) => {
        const { userDetails } = this.props;
        if (row.id) {
            this.setState({ viewTransactionId: row.id, viewTransactionGroup: null });
        } else {
            this.setState({
                viewTransactionId: null,
                viewTransactionGroup: { userId: userDetails?.id, forDate: row.for_date, overtimeType: row.overtime_type },
            });
        }
    }

    handleOvertimeAdded = () => {
        const { reloadOvertimeBalance } = this.props;
        this.setState({ showAddModal: false });
        if (reloadOvertimeBalance) reloadOvertimeBalance();
    }

    handleYearChange = (newYear) => {
        const { overtimeBankStore, onYearChange } = this.props;
        // The date-range filter is only ever meaningful within the selected year - clear it on
        // every year change rather than leaving a stale range that silently ANDs with the new
        // year and can zero out the results with no indication why.
        overtimeBankStore.setFilter('year', newYear);
        overtimeBankStore.setFilter('fromDate', null);
        overtimeBankStore.setFilter('fromDate1', null);
        overtimeBankStore.setFilter('toDate', null);
        overtimeBankStore.setFilter('toDate1', null);
        overtimeBankStore.onFilter();
        // Lets the parent refresh the header stat pills for the currently selected member against
        // the new year (see OvertimeBank.js's handleYearChange) - unrelated to the transactions
        // list refetch above, which GenericList already triggers on its own via appliedFilters.
        if (onYearChange) onYearChange(newYear);
    }

    render() {
        const { t, overtimeBankStore, userDetails, commonStore, isMember, currentUserType } = this.props;
        const { filters, appliedFilters, loading } = overtimeBankStore;
        const { showAddModal, viewTransactionId, viewTransactionGroup } = this.state;
        const yearOptions = generateYearOptions(new Date().getFullYear() - 4, 5).map(y => ({ value: Number(y), label: y }));
        const selectedYear = filters.year || new Date().getFullYear();
        const dateFilterMinDate = new Date(selectedYear, 0, 1);
        const dateFilterMaxDate = new Date(selectedYear, 11, 31);
        const dateTimeRules = commonStore.getDateTimeRules();
        const overtimeBankSettings = commonStore.config?.client?.data?.basicRules?.overtimeBankSettings || {};
        const paidOvertimeEnabled = !!(overtimeBankSettings.allowOvertimeBank && overtimeBankSettings.allowPaidOvertimeBalanceManagement);
        const canManagePaidOvertime = !isMember && paidOvertimeEnabled && (currentUserType === 'admin' || overtimeBankSettings.allowPmToManagePaidOvertimeBalance);
        const totalOvertimeMins = userDetails?.total_hours_in_mins || 0;
        const paidOvertimeBalance = userDetails?.paid_overtime_balance_in_mins || 0;
        const showOvertimeSlider = paidOvertimeEnabled && paidOvertimeBalance > 0;
        const overtimeUsagePercentage = showOvertimeSlider ? (totalOvertimeMins / paidOvertimeBalance) * 100 : 0;
        const memberInfoLines = [
            `${t('Emp. Id.')}: ${userDetails?.internal_number || ''}`,
            userDetails?.user_type ? t(user_type_map[userDetails.user_type] || userDetails.user_type) : '',
        ];


        return (
            <div className='mt-2'>
                <Row>
                    <Col md={4} className='d-flex align-items-center'>
                        {!isMember &&
                            <MemberInfoCard
                                imageId={userDetails?.image ? userDetails?.image : false}
                                name={userDetails?.name}
                                lines={memberInfoLines}
                            />
                        }
                    </Col>
                    <Col md={8} className='d-flex justify-content-end align-items-center flex-wrap gap-2'>
                        <div style={{ minWidth: 110 }}>
                            <Select
                                options={yearOptions}
                                value={yearOptions.find(o => o.value === selectedYear) || null}
                                onChange={(opt) => this.handleYearChange(opt.value)}
                                isSearchable={false}
                                placeholder={t('Year')}
                            />
                        </div>
                        {showOvertimeSlider ?
                            <StatSliderPill
                                label={t('Overtime')}
                                value={`${totalTimeFormat(totalOvertimeMins, dateTimeRules)} / ${totalTimeFormat(paidOvertimeBalance, dateTimeRules)}`}
                                percentage={overtimeUsagePercentage}
                                tooltip={`${Math.round(overtimeUsagePercentage)}% ${t('of paid overtime balance has been used out of')} ${totalTimeFormat(paidOvertimeBalance, dateTimeRules)} ${t('hours')}`}
                            />
                            :
                            <StatPill items={[{ label: t('Overtime'), value: totalTimeFormat(totalOvertimeMins, dateTimeRules) }]} />
                        }
                        {paidOvertimeEnabled &&
                            <StatPill items={[{ label: t('Paid Overtime'), value: totalTimeFormat(paidOvertimeBalance, dateTimeRules) }]} />
                        }
                        {canManagePaidOvertime &&
                            <Button fill wd onClick={() => this.setState({ showAddModal: true })}>
                                <i className="fa fa-plus-circle fa-xl margin-right-10" /> {t('Paid Overtime')}
                            </Button>
                        }
                    </Col>
                </Row>
                {showAddModal &&
                    <AddPaidOvertimeModal
                        userDetails={userDetails}
                        onClose={() => this.setState({ showAddModal: false })}
                        onSaved={this.handleOvertimeAdded}
                    />
                }
                {(viewTransactionId || viewTransactionGroup) &&
                    <ViewOvertimeTransactionModal
                        transactionId={viewTransactionId}
                        groupParams={viewTransactionGroup}
                        onClose={() => this.setState({ viewTransactionId: null, viewTransactionGroup: null })}
                    />
                }
                <GenericList
                    columns={[
                        {
                            Header: 'Date',
                            id: 'for_date',
                            accessor: ({ for_date }) => <span>{shortDateFormat(for_date, dateTimeRules)}</span>,
                            maxWidth: 70,
                            minWidth: 70
                        },
                        {
                            Header: 'Hours',
                            id: 'overtime_hours_in_mins',
                            accessor: ({ overtime_hours_in_mins, transaction_count }) => (
                                <span>
                                    {totalTimeFormat(overtime_hours_in_mins, dateTimeRules)}
                                    {transaction_count > 1 &&
                                        <span className='badge bg-secondary ms-1'>{transaction_count}</span>
                                    }
                                </span>
                            ),
                            maxWidth: 70,
                            minWidth: 70
                        },
                        {
                            Header: 'Overtime Type',
                            id: 'overtime_type',
                            accessor: ({ overtime_type }) => (
                                <div className='d-flex justify-content-center'>
                                    <StatusBadge
                                        label={t(overtime_transaction_overtime_type_map[overtime_type] || overtime_type)}
                                        color={overtime_transaction_overtime_type_color_map[overtime_type]}
                                        outline
                                    />
                                </div>
                            ),
                            maxWidth: 90,
                            minWidth: 90
                        },
                        {
                            Header: 'Transaction Type',
                            id: 'transaction_type',
                            accessor: ({ transaction_type }) => (
                                <div className='d-flex justify-content-center'>
                                    <StatusBadge
                                        label={t(overtime_transaction_type_map[transaction_type] || transaction_type)}
                                        color={overtime_transaction_type_color_map[transaction_type]}
                                        outline
                                    />
                                </div>
                            ),
                            maxWidth: 90,
                            minWidth: 90
                        },
                        {
                            Header: 'Reason',
                            id: 'reason',
                            accessor: ({ reason, reason_text }) => <span>{(reason_text ? t(reason_text) : reason) || '-'}</span>,
                            textAlign: 'start'
                        },
                        {
                            Header: 'Created At',
                            id: 'created_at',
                            accessor: ({ created_at }) => <span>{shortDateFormat(created_at, dateTimeRules)} {dateToTimeFormat(created_at, dateTimeRules)}</span>,
                            maxWidth: 90,
                            minWidth: 90
                        },
                        {
                            Header: 'Status',
                            id: 'status',
                            accessor: ({ status, created_by_name }) => (
                                <div className='d-flex justify-content-center'>
                                    <StatusBadge
                                        label={
                                            <div className='d-flex flex-column align-items-center'>
                                                <span>{t(overtime_transaction_status_map[status] || status)}</span>
                                                {created_by_name && <span className='fs10'>{created_by_name}</span>}
                                            </div>
                                        }
                                        color={overtime_transaction_status_color_map[status]}
                                    />
                                </div>
                            ),
                            maxWidth: 100,
                            minWidth: 100
                        },
                        {
                            Header: 'Actions',
                            id: 'actions',
                            sortable: false,
                            accessor: (row) => (
                                <div className='d-flex justify-content-center'>
                                    <OverlayTrigger placement="top" overlay={<Tooltip id={`view-transaction-${row.id || `${row.for_date}-${row.overtime_type}`}`}>{t('View Details')}</Tooltip>}>
                                        <Button icon_sm fill onClick={() => this.handleViewRow(row)}>
                                            <i className='fa fa-eye' />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            ),
                            maxWidth: 70,
                            minWidth: 70
                        },
                    ]}
                    header={
                        <GenericFilter
                            filters={filters}
                            setFilter={(name, value) => overtimeBankStore.setFilter(name, value)}
                            onFilter={() => overtimeBankStore.onFilter()}
                            loading={loading}
                            hasSearch={false}
                            hasDateFilters={true}
                            dateFilterMinDate={dateFilterMinDate}
                            dateFilterMaxDate={dateFilterMaxDate}
                            hasStatusFilter={true}
                            statusOptions={overtime_transaction_types}
                            statusAllLabel={t("All Transactions")}
                            hasCustomFilter={true}
                            customFilterOptions={[{ value: '', label: t('All Overtimes') }, ...overtime_transaction_overtime_types.map(o => ({ value: o.code, label: t(o.name) }))]}
                            customFilterName={{ value: 'overtime_type', label: t('Overtime Type') }}
                        />
                    }
                    filters={appliedFilters}
                    requestData={(params) => overtimeBankStore.loadOvertimeTransactions(params)}
                    lastListLoadTime={overtimeBankStore.lastListLoadTime}
                />
            </div>
        );
    }
}

export default inject('commonStore', 'overtimeBankStore')(applicationRouter(withLocalization(observer(OvertimeBankList))));
